import { useEffect, useState } from 'react'
import { fetchAccountInfo, type AccountInfo } from '../../services/account'
import StatCard from '../ui/StatCard'

export default function UsagePanel() {
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAccountInfo()
      .then(setAccount)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="font-mono text-sm text-slate-500 animate-pulse">Loading usage data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-400/5 border border-red-400/20 p-4">
        <p className="font-mono text-sm text-red-400">{error}</p>
      </div>
    )
  }

  if (!account) return null

  const quotaPct = account.jobsPerMonth > 0
    ? Math.min(100, (account.jobsUsed / account.jobsPerMonth) * 100)
    : 0

  const barColor = quotaPct >= 90 ? '#f87171' : quotaPct >= 70 ? '#fbbf24' : '#06b6d4'
  const barGlow = quotaPct < 70 ? '0 0 8px rgba(6,182,212,0.3)' : undefined

  const planLabels: Record<string, string> = {
    invited: 'Invited',
    free: 'Free',
    pro: 'Pro',
  }

  return (
    <div className="space-y-6">
      {/* Job Quota */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-4">
          Project Quota
        </h3>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="font-mono text-2xl text-slate-200">{account.jobsUsed}</span>
          <span className="font-mono text-sm text-slate-500">/ {account.jobsPerMonth} projects this month</span>
        </div>
        <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${quotaPct}%`,
              backgroundColor: barColor,
              boxShadow: barGlow,
            }}
          />
        </div>
        <p className="font-mono text-xs text-slate-500 mt-2">
          {account.jobsRemaining} remaining
        </p>
      </div>

      {/* API Usage */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-4">
          API Usage This Month
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Calls" value={account.apiUsage.totalCalls.toLocaleString()} />
          <StatCard label="Tokens" value={account.apiUsage.totalTokens.toLocaleString()} />
          <StatCard label="Est. Cost" value={`$${account.apiUsage.estimatedCostUsd.toFixed(2)}`} />
        </div>
      </div>

      {/* Account Status */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-4">
          Account
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-slate-500">Plan</span>
            <span className="font-mono text-sm text-cyan-400">
              {planLabels[account.plan] ?? account.plan}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-slate-500">Member since</span>
            <span className="font-mono text-sm text-slate-300">
              {new Date(account.memberSince).toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </div>
        </div>

        <button
          disabled
          className="mt-6 w-full py-2 glass rounded-lg font-mono text-xs text-amber-400/60 border border-amber-500/20 cursor-not-allowed"
        >
          Upgrade Plan (Coming Soon)
        </button>
      </div>
    </div>
  )
}
